"use client";

import React from "react";
import { HeartHandshake, Map, Clock, CircleCheck, ShieldCheck, UserCheck, MessageSquare, PhoneCall } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const features = [{
  icon: HeartHandshake,
  title: "עזרנו למאות משפחות",
  description: "אנו מלווים משפחות רבות ברגעים החשובים ביותר, במסירות, רגישות ומקצועיות ללא פשרות."
}, {
  icon: Map,
  title: "פריסה ארצית",
  description: "שירות זמין ונגיש בכל רחבי הארץ, כדי להיות שם בשבילכם בדיוק היכן ומתי שתצטרכו אותנו."
}, {
  icon: Clock,
  title: "מענה תוך שעות",
  description: "צוות המומחים שלנו זמין עבורכם למענה מהיר, יעיל ומקצועי, תוך הבנת הדחיפות והחשיבות."
}];
const faqs = [{
  question: "תוך כמה זמן המטפל מגיע?",
  answer: "אנו מתחייבים להגעת מטפל ישראלי תוך 5 שעות מרגע הפנייה, בפריסה ארצית מלאה. אנו מבינים שמצבי סיעוד דחופים אינם סובלים דיחוי, ולכן המערך הלוגיסטי שלנו בנוי לתגובה מהירה במיוחד, בין אם מדובר בבית המטופל או בבית החולים."
}, {
  question: "האם יש לכם מטפלים דוברי עברית?",
  answer: "כן, כל המטפלים שלנו הם ישראלים דוברי עברית כשפת אם. אנו מאמינים שתקשורת היא הבסיס לטיפול איכותי. היכולת של המטופל להביע את צרכיו, רגשותיו וכאביו בשפתו, והיכולת של המטפל להבין ניואנסים תרבותיים, היא קריטית לשקט הנפשי של המשפחה ולרווחת המטופל."
}, {
  question: "האם השירות מיועד גם לבתי חולים?",
  answer: "בהחלט. אנו מספקים שירותי השגחה וטיפול צמודים (פרטיים) בכל בתי החולים והמוסדות הרפואיים בישראל. השירות שלנו משלים את הטיפול המוסדי ומעניק למטופל תשומת לב אישית 1-על-1, מה שמפחית את הסיכון לנפילות, טעויות בטיפול ומשפר את תהליך ההחלמה."
}, {
  question: "איך מתבצע תהליך ההתאמה בין המטפל למטופל?",
  answer: "תהליך ההתאמה אצלנו ב-Siudy הוא יסודי ומהיר. אנו בוחנים את הצרכים הרפואיים, המגבלות הפיזיות, והעדפות האישיות של המטופל. אנו מוודאים שהמטפל שנשלח הוא בעל הניסיון הרלוונטי (למשל, ניסיון עם חולי דמנציה או שיקום לאחר ניתוח) ובעל אישיות שתשתלב היטב בדינמיקה המשפחתית."
}, {
  question: "מה קורה אם המטפל לא מתאים לנו לאחר שהגיע?",
  answer: "הביטחון שלכם הוא מעל הכל. אם מכל סיבה שהיא אתם מרגישים שאין חיבור נכון או שהמטפל אינו עונה על הציפיות, אנו מחויבים להחלפה מהירה ככל הניתן. אנו עומדים מאחורי איכות השירות שלנו ומבצעים בקרה שוטפת לאורך כל תקופת הטיפול."
}];
const benefitsList = [{
  id: 1,
  text: "מטפלים ישראלים דוברי עברית בלבד"
}, {
  id: 2,
  text: "זמינות מיידית - הגעה תוך עד 5 שעות"
}, {
  id: 3,
  text: "שירות מותאם אישית לבית ולבית החולים"
}, {
  id: 4,
  text: "ליווי מקצועי וזמינות 24/7 לכל שאלה"
}];
export default function TrustAndSecurity() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="אמון-וביטחון" className="relative bg-background">
      <div className="container mx-auto px-4 py-[120px]">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-16 max-w-5xl text-right">
            <p className="mb-4 text-lg font-medium text-chart-2">ביטחון ושקט נפשי למשפחה</p>
            <h1 className="mb-8 text-5xl font-extrabold leading-tight text-primary md:text-6xl">
              אמון וביטחון בטיפול הסיעודי: המדריך המלא לבחירת מטפל סיעודי בישראל
            </h1>
            
            <h2 className="mb-6 text-3xl font-bold text-primary">למה ביטחון הוא המפתח בטיפול סיעודי?</h2>
            <p className="mb-8 text-xl leading-relaxed text-muted-foreground">
              ב-Siudy, אנו מאמינים שטיפול סיעודי איכותי מתחיל באמון מלא. כאשר אתם מפקידים את היקר לכם מכל בידיים של אדם אחר, הביטחון שלכם ושל יקיריכם חייב להיות בראש סדר העדיפויות. אנו פועלים בשקיפות מלאה כדי להבטיח לכם שקט נפשי ברגעים המורכבים ביותר. הניסיון שלנו מלמד שמשפחות רבות חוות לחץ עצום בעת משבר רפואי, והצורך במענה מהיר, אמין ומקצועי הוא קריטי לא רק עבור המטופל, אלא עבור המערך המשפחתי כולו.
            </p>

            <div className="mb-12 space-y-6 text-lg leading-relaxed text-muted-foreground">
              <p>
                בחירת מטפל סיעודי היא אחת ההחלטות המשמעותיות ביותר שתקבלו. זהו לא רק שירות טכני, אלא הכנסת דמות חדשה למרחב האישי והאינטימי ביותר. לכן, ב-Siudy אנו מקפידים על תהליך מיון קפדני הכולל בדיקת רקע, המלצות וראיונות עומק. אנו מבינים שהשוק הישראלי רווי באפשרויות, אך המחסור במטפלים דוברי עברית וזמינים יוצר פער מסוכן. הפער הזה הוא בדיוק המקום שבו אנו נכנסים לתמונה. למידע נוסף על הזכויות המגיעות לכם, תוכלו לבקר ב-<Link to="/ProfessionalBlogPage" className="font-bold text-chart-2 underline">בלוג המקצועי שלנו</Link> שם אנו מפרטים על זכויות ביטוח לאומי וטיפים לבחירת מטפל.
              </p>
              <p>
                המודל שלנו מבוסס על שלושה עמודי תווך: מקצועיות, זמינות ורגישות תרבותית. המטפלים שלנו הם ישראלים המכירים את המערכת הרפואית בארץ, יודעים כיצד לתקשר עם הצוותים בבתי החולים ומבינים את הצרכים הייחודיים של הגיל השלישי בישראל. זהו יתרון משמעותי במניעת אי-הבנות שעלולות להוביל לטעויות בטיפול או לתחושת בדידות של המטופל.
              </p>
            </div>
            
            <h3 className="mb-6 text-2xl font-bold text-primary">המענה המהיר והמקצועי ביותר בישראל - הגעה תוך 5 שעות</h3>
            <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
              אנו פותרים את המחסור במטפלים זמינים על ידי חיבור מיידי למטפלים ישראלים מנוסים תוך 5 שעות בלבד. השירות שלנו מאפשר לכם להסיר את דאגת הטיפול מסדר היום ולהתמקד בליווי המשפחתי. המהירות שלנו היא לא רק עניין של נוחות, אלא צורך רפואי. במקרים של שחרור מבית חולים או החמרה פתאומית במצב הבריאותי, כל שעה קובעת. הצוות הלוגיסטי שלנו פרוס ב-<Link to="/CaregiverServicePagesPage" className="font-bold text-chart-2 underline">עשרות ערים ברחבי הארץ</Link>, מה שמאפשר לנו להגיע לכל נקודה במהירות שיא.
            </p>

            <div className="mb-12 space-y-6 text-lg leading-relaxed text-muted-foreground">
              <p>
                תהליך העבודה שלנו פשוט ויעיל: מרגע הפנייה שלכם, יועץ סיעוד מומחה מנתח את הצרכים שלכם ובוחר את המטפל המתאים ביותר מתוך מאגר המטפלים המאושרים שלנו. אנו לוקחים בחשבון פרמטרים כמו מיקום גיאוגרפי, סוג הטיפול הנדרש (השגחה, עזרה ברחצה, האכלה, ליווי רפואי) והתאמה אישיותית. המטפל מגיע אליכם עם כל המידע הרלוונטי ומתחיל בטיפול מיידי, תוך ליווי של המוקד המקצועי שלנו 24/7.
              </p>
              <p>
                חשוב להבין שטיפול סיעודי פרטי מעניק שכבת הגנה נוספת שאינה קיימת במערכת הציבורית העמוסה. בבית החולים, האחיות והרופאים עושים עבודת קודש, אך הם אינם יכולים להיות לצד המטופל בכל רגע נתון. מטפל של Siudy נמצא שם כדי לוודא שהמטופל לא יורד מהמיטה ללא השגחה, שהוא שותה מספיק, ושהוא מקבל את תשומת הלב הרגשית לה הוא זקוק.
              </p>
            </div>
          </div>

          <div className="mb-16 text-right">
            <h4 className="mb-8 text-2xl font-bold text-primary">למה לבחור ב-Siudy לטיפול הסיעודי שלכם?</h4>
            <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:max-w-4xl">
              {benefitsList.map((benefit, i) => <li key={benefit.id} data-index={i} className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-sm transition-transform hover:scale-[1.02]">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-chart-2/10">
                    <CircleCheck className="h-6 w-6 text-chart-2" />
                  </div>
                  <span className="text-xl font-medium text-foreground">{benefit.text}</span>
                </li>)}
            </ul>
          </div>

          <div className="mb-16 max-w-5xl text-right">
            <h3 className="mb-6 text-2xl font-bold text-primary">הסטנדרטים שלנו למקצועיות וחמלה</h3>
            <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
              <p>
                הניסיון שלנו מראה שטיפול סיעודי הוא הרבה מעבר לעזרה טכנית. מדובר בקשר אנושי, בחמלה ובביטחון שהמטופל נמצא בידיים הכי טובות שיש. כל מטפל בצוות שלנו נבחר בקפידה ועובר תהליך סינון מחמיר שכולל לא רק בדיקת יכולות מקצועיות, אלא גם מבחני אמינות ואישיות. אנו מחפשים אנשים עם "לב גדול" – כאלה שרואים במקצוע הסיעוד שליחות ולא רק עבודה.
              </p>
              <p>
                בנוסף, אנו משקיעים רבות בהדרכה שוטפת של הצוותים שלנו. עולם הסיעוד משתנה, וטכנולוגיות חדשות לצד גישות טיפוליות מתקדמות נכנסות לשימוש. אנו מוודאים שהמטפלים שלנו מעודכנים בשיטות העבודה הטובות ביותר, תוך דגש על בטיחות המטופל ומניעת זיהומים. השירות שלנו כולל גם בקרה טלפונית וביקורי פתע (במידת הצורך) כדי לוודא שרמת השירות נשמרת גבוהה לאורך זמן.
              </p>
              <p>
                אם אתם מתלבטים לגבי סוג הטיפול המתאים לכם, אנו מזמינים אתכם ל-<Link to="/CaregiverServicePagesPage" className="font-bold text-chart-2 underline">ליצור איתנו קשר לייעוץ ראשוני ללא התחייבות</Link>. המומחים שלנו יקשיבו לסיפור שלכם ויעזרו לכם לבנות תוכנית טיפול שתעניק ליקירכם את המקסימום ולכם את השקט הנפשי המגיע לכם.
              </p>
              <p>
                לסיכום, אמון נבנה במעשים, לא במילים. הזמינות המיידית שלנו, האיכות הבלתי מתפשרת של המטפלים הישראלים והליווי הצמוד של המשפחה הם שהופכים את Siudy לבחירה המועדפת על מאות משפחות בישראל. אנו כאן כדי להבטיח שאף מטופל לא יישאר לבד, ואף משפחה לא תצטרך להתמודד עם נטל הסיעוד ללא תמיכה מקצועית ואמינה.
              </p>
            </div>
          </div>

          <div className="mb-16 text-right">
            <h2 className="mb-8 text-3xl font-bold text-primary">שאלות ותשובות נפוצות על שירותי סיעוד מיידיים</h2>
            <Accordion type="single" collapsible className="w-full max-w-4xl space-y-4">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="rounded-xl border border-border bg-card px-4 shadow-sm">
                  <AccordionTrigger className="text-right text-lg font-bold hover:no-underline">
                    <span className="flex items-center gap-3">
                      <MessageSquare className="h-5 w-5 text-chart-3" />
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-right text-base leading-relaxed text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          <div className="mb-20 rounded-2xl bg-primary/5 p-8 text-right md:p-12">
            <div className="max-w-3xl">
              <h3 className="mb-4 text-3xl font-bold text-primary">מוכנים להתחיל בטיפול?</h3>
              <p className="mb-8 text-xl text-muted-foreground">
                אל תחכו לרגע האחרון. צרו קשר עכשיו וקבלו מטפל ישראלי מנוסח תוך פחות מ-5 שעות. הצוות שלנו זמין עבורכם לכל שאלה.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="tel:0500000000">
                  <Button size="lg" className="h-16 rounded-lg bg-chart-3 px-12 text-xl font-bold text-white shadow-lg shadow-chart-3/20 hover:bg-chart-3/90">
                    <PhoneCall className="ml-2 h-6 w-6" />
                    התקשרו עכשיו לייעוץ
                  </Button>
                </Link>
                <Link to="https://wa.me/972500000000" newTab>
                  <Button size="lg" variant="outline" className="h-16 rounded-lg border-chart-2 px-12 text-xl font-bold text-chart-2 hover:bg-chart-2/10">
                    שלחו הודעת וואטסאפ
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            return <div key={i} data-index={i} className="flex flex-col items-center text-center">
                  <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-lg bg-chart-2/10 transition-colors hover:bg-chart-2/20">
                    <Icon className="h-12 w-12 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="mb-4 font-default text-2xl font-bold text-primary">
                    {feature.title}
                  </h3>
                  <p className="max-w-[280px] font-default text-base font-normal leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full px-4 md:px-8">
        <Separator className="w-full bg-border" />
      </div>
    </section>;
}